import type {
  CheckoutDto,
  CheckoutResponse,
  Order,
  OrderListItem,
} from '@printtat/shared';
import { apiFetch, apiJson } from '@/lib/apiFetch';
import { ApiError } from '@/lib/apiError';

export interface ReorderResponse {
  orderId: string;
  orderNumber: string;
}

export const orderApi = {
  async checkout(dto: CheckoutDto): Promise<CheckoutResponse> {
    return apiJson<CheckoutResponse>('/orders/checkout', {
      method: 'POST',
      body: dto,
    });
  },

  async getOrders(): Promise<OrderListItem[]> {
    return apiJson<OrderListItem[]>('/orders');
  },

  async getOrder(id: string): Promise<Order> {
    return apiJson<Order>(`/orders/${id}`);
  },

  async cancelOrder(id: string): Promise<Order> {
    return apiJson<Order>(`/orders/${id}/cancel`, { method: 'POST' });
  },

  // Re-order surfaces a structured { code } body (PAYMENT_RETRY_LIMIT, etc.)
  // — keep the raw fetch path so we can attach the code to ApiError.
  async reorder(id: string): Promise<ReorderResponse> {
    const res = await apiFetch(`/orders/${id}/reorder`, { method: 'POST' });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const body = (e?.message ?? e) as
        | { code?: string; message?: string }
        | string;
      const message =
        typeof body === 'string' ? body : body?.message || 'Failed to re-order';
      const code = typeof body === 'object' ? body?.code : undefined;
      throw new ApiError(message, res.status, code);
    }
    return (await res.json()) as ReorderResponse;
  },
};
